import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  NativeSelect,
  styled,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography
} from '@mui/material'
import { GridToolbarContainer } from '@mui/x-data-grid'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { useQuery } from '@tanstack/react-query'

import BillAPI from '../../../api/orbitV1/BillAPI'
import { PDF } from '../../../assets/icons/circles/PDF'
import CloseIcon from '../../../assets/icons/circles-design/Close.png'
import image from '../../../assets/icons/circles-design/VectorRefresh.svg'
import CustomSearchbar from '../../../common/AbstractDataGrid/CustomSearchbar'
import MUIDataGrid from '../../../common/AbstractDataGrid/DataGridMUI'
import PageLayout from '../../../common/Layout/Page/PageLayout'
import TableSkeleton from '../../../common/Skeletons/TableSkeleton'
import chips from '../../../common/ui/chips'
import { ColorBoxContent } from '../../../common/ui/colorBox'
import { getDate } from '../../../helpers/date'
import ConfirmQuotationModal from '../../Quotations/components/ConfirmQuotationModal'

let toggledWarningFromPrevLocation = false

/**
 *
 * @returns
 */
export default function BillManagement() {
  const [isDialogOpen, setDialogOpen] = useState(false)
  const [billBtnDisabled, setBillBtnDisabled] = useState(false)
  const [allBillDetails, setAllBillDetails] = useState([])
  // for dropdown status select in modal
  const [status, setStatus] = useState('')
  const [billDetailsIdx] = useState(0)
  // note: should use the join date for the organization
  const MIN_DATE = new Date('2016-01-01')
  const MAX_DATE = new Date()
  const navigate = useNavigate()
  const location = useLocation()

  if (location.state?.reason && !toggledWarningFromPrevLocation) {
    toggledWarningFromPrevLocation = true
    toast.error(location.state.reason)
    delete location.state.reason
  }

  const getBillPdf = async (selectedBill) => {
    const billPromise = BillAPI.getBillPdf(selectedBill.billNo)
    toast.promise(billPromise, {
      pending: 'Retrieving your bill, please wait...',
      success: `Successfully downloaded bill ${selectedBill.billNo}`,
      error: 'Error downloading Bill. Please try again later!'
    })
  }
  const VectorRefresh = async () => {
    setBillBtnDisabled(true)
    setTimeout(() => setBillBtnDisabled(false), 5 * 60 * 1000)
    const res = BillAPI.generateBills()

    if (res) {
      toast.success('Triggered ad hoc cron billing API job')
    } else {
      toast.error('Unexpected error trigger ad hoc cron billing API job')
    }
  }

  // set start date to be three months before current
  const defaultStartDate = new Date()
  defaultStartDate.setDate(1)
  defaultStartDate.setMonth(new Date().getMonth() - 3)

  // set end date to be current month
  const defaultEndDate = new Date()
  defaultEndDate.setMonth(defaultEndDate.getMonth())
  defaultEndDate.setDate(1)

  const [startDate, setStartDate] = useState(defaultStartDate)
  const [endDate, setEndDate] = useState(defaultEndDate)

  const handleChange = (event) => {
    setStatus(event.target.value)
  }

  /* useQuery to retrieve bills */
  const {
    data: retrievedAllBills,
    error: retrievedAllBillsError,
    isLoading,
    refetch
  } = useQuery(['getAllBillsWithinPeriod', startDate, endDate], async () => {
    const { data } = await BillAPI.getAllBills(startDate, endDate)
    return data
  })

  useEffect(() => {
    if (retrievedAllBills) {
      setAllBillDetails(retrievedAllBills)
    }
  }, [retrievedAllBills])

  if (retrievedAllBillsError) {
    toast.error('Error retrieving bills. Please try again later!')
  }

  /* Data Grid Components */
  const rows = allBillDetails.map((bill) => {
    return {
      id: bill.billNo,
      invoiceDate: getDate(bill.createdAt),
      invoiceNo: bill.billNo.substring(0, 10) + '...',
      company: bill.orgName,
      billNo: bill.billNo,
      billingPeriod: bill.billPeriod,
      amount: bill.amount,
      transactionNumber: bill.transactionNumber
        ? bill.transactionNumber.substring(0, 10) + '...'
        : '-',
      paymentMethod: bill.paymentMethod,
      status: bill.status,
      _raw: bill
    }
  })

  const columns = [
    { field: 'invoiceDate', headerName: 'Invoice Date', width: 100 },
    { field: 'invoiceNo', headerName: 'Invoice No.', width: 100 },
    { field: 'company', headerName: 'Company', width: 100 },
    { field: 'billingPeriod', headerName: 'Billing Period', width: 50 },
    { field: 'amount', headerName: 'Amount', width: 50 },
    { field: 'transactionNumber', headerName: 'Transaction No.', width: 100 },
    { field: 'paymentMethod', headerName: 'Payment Method', width: 50 },
    {
      field: 'status',
      headerName: 'Status',
      width: 50,
      renderCell: (params) => {
        const _status = params.row.status
        switch (_status) {
          case 'DUE':
            return chips.bills.DUE

          case 'PAID':
            return chips.bills.PAID

          default:
            break
        }
      }
    },
    {
      field: 'actions',
      headerName: 'Action',
      width: 50,
      renderCell: (params) => {
        const bill = params.row

        return (
          <IconButton
            // TODO: implement View action
            onClick={() => {
              getBillPdf(bill)
            }}
          >
            <PDF />
          </IconButton>
        )
      }
    }
  ]

  const AccountToolbar = () => {
    return (
      <GridToolbarContainer
        sx={{
          justifyContent: 'space-between'
        }}
      >
        <CustomSearchbar />

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button onClick={VectorRefresh} disabled={billBtnDisabled}>
            <img
              src={image}
              alt='VectorRefresh'
              style={{
                height: '20px',
                marginLeft: 20
              }}
            />
          </Button>
          <Box>
            <FormControl variant='standard' sx={{ m: 1, minWidth: 120 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  views={['year', 'month']}
                  label='Start Date'
                  value={startDate}
                  minDate={MIN_DATE}
                  maxDate={MAX_DATE}
                  onChange={(newValue) => {
                    setStartDate(newValue)
                  }}
                  renderInput={(params) => (
                    <TextField {...params} helperText={null} />
                  )}
                />
              </LocalizationProvider>
            </FormControl>
            <FormControl variant='standard' sx={{ m: 1, minWidth: 120 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  views={['year', 'month']}
                  label='End Date'
                  value={endDate}
                  minDate={MIN_DATE}
                  maxDate={MAX_DATE}
                  onChange={(newValue) => {
                    setEndDate(newValue)
                  }}
                  renderInput={(params) => (
                    <TextField {...params} helperText={null} />
                  )}
                />
              </LocalizationProvider>
            </FormControl>
          </Box>
        </Box>
      </GridToolbarContainer>
    )
  }

  const handlePaymentStatusChange = async () => {
    if (
      allBillDetails[billDetailsIdx]?.status === 'PENDING' ||
      allBillDetails[billDetailsIdx]?.status === 'DUE'
    ) {
      // update billing status

      try {
        const updateBillRes = await BillAPI.updateBill(
          `${allBillDetails[billDetailsIdx].uen}-${allBillDetails[billDetailsIdx].billNo}`
        )

        if (updateBillRes) {
          setDialogOpen(false)

          toast.success('Submitted!', {
            autoClose: 3000,
            hideProgressBar: false
          })
          setTimeout(() => {
            navigate('manage-bill')
          }, 2000)
        } else {
          toast.error('Unexpected error updating payment status')
        }
      } catch (err) {
        throw new Error(err)
      }
    }
  }
  useEffect(() => {
    refetch()
  }, [startDate, endDate])

  // Style table cell to remove top bottom padding
  const StyledCell = styled(TableCell)({
    height: '10px',
    padding: '0 16px'
  })

  return (
    <PageLayout header='Manage Bills'>
      {isLoading ? (
        <TableSkeleton />
      ) : (
        <ColorBoxContent>
          <Box>
            <MUIDataGrid
              initialState={{
                sorting: {
                  sortModel: [{ field: 'invoiceDate', sort: 'desc' }]
                }
              }}
              rows={rows}
              columns={columns}
              components={{ Toolbar: AccountToolbar }}
            />
          </Box>
          {isDialogOpen && <ConfirmQuotationModal closeModal={setDialogOpen} />}
          <Dialog
            PaperProps={{
              style: {
                borderRadius: '8px',
                minWidth: '50%',
                minHeight: '50%'
              }
            }}
            open={isDialogOpen}
            onClose={() => setDialogOpen(false)}
          >
            <DialogTitle sx={{ padding: '22px 38px' }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between'
                }}
              >
                <Typography variant='h4'>Edit Payment Status</Typography>
                <IconButton
                  aria-label='close'
                  onClick={() => setDialogOpen(false)}
                  sx={{
                    color: (_theme) => _theme.palette.grey[500]
                  }}
                >
                  <img src={CloseIcon} width='24px' alt='Close Icon' />
                </IconButton>
              </Box>
              <Divider />
            </DialogTitle>
            <DialogTitle>
              <Box sx={{ marginBottom: '17px' }}>
                <Table>
                  <TableCell>
                    <TableRow>
                      <StyledCell align='left'>
                        <Typography variant='h6'>Transaction No.</Typography>
                      </StyledCell>
                      <StyledCell align='left'>
                        <Typography variant='h6'>Payment Method</Typography>
                      </StyledCell>
                      <StyledCell align='left'>
                        <Typography variant='h6'>Status</Typography>
                      </StyledCell>
                    </TableRow>
                  </TableCell>
                  <TableBody>
                    <TableRow>
                      <StyledCell scope='bill'>
                        <Typography>
                          {allBillDetails[billDetailsIdx]?.transactionNumber}
                        </Typography>
                      </StyledCell>
                      <StyledCell scope='bill'>
                        <Typography>
                          {allBillDetails[billDetailsIdx]?.paymentMethod}
                        </Typography>
                      </StyledCell>
                      <StyledCell scope='bill'>
                        <Typography
                          sx={{
                            color: '#818D9C',
                            margin: '12px'
                          }}
                        >
                          <Box>
                            <FormControl
                              variant='standard'
                              sx={{ minWidth: 120 }}
                            >
                              <NativeSelect
                                sx={{ border: '10px', paddingLeft: '45px' }}
                                id='demo-select-small'
                                value={status}
                                label='status'
                                onChange={handleChange}
                              >
                                <option value='DUE'>{chips.bills.DUE}</option>
                                <option value='PAID'>{chips.bills.PAID}</option>
                              </NativeSelect>
                            </FormControl>
                          </Box>
                        </Typography>
                      </StyledCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            </DialogTitle>
            <DialogTitle>
              <DialogContent />
            </DialogTitle>
            <DialogActions>
              <Button
                variant='filled'
                isDialogOpen={isDialogOpen}
                closeModal={setDialogOpen}
                onClick={handlePaymentStatusChange}
              >
                Save
              </Button>
            </DialogActions>
          </Dialog>
        </ColorBoxContent>
      )}
    </PageLayout>
  )
}
